import * as Status from '../../../constants/status';
import {
  isInspectionFieldStatus,
  isTipoServicoDigitacaoAntecipada,
} from '../../../helpers/digitacaoAntecipada';

export const DIGITADOR_BATCH_STATUSES = [
  Status.INSPECAO_FINALIZADA,
  Status.AGUARDANDO_DIGITACAO,
];

export const INSPETOR_BATCH_STATUSES = [
  Status.EM_CRIACAO,
  Status.AGUARDANDO_INSPECAO,
  Status.INSPECAO_ENVIADA_PARCIAL,
];

export const isDigitadorBatchStatus = (status) =>
  DIGITADOR_BATCH_STATUSES.includes(status);

export const isInspetorBatchStatus = (status) =>
  INSPETOR_BATCH_STATUSES.includes(status);

const getRowMeta = (statusById, id) => {
  const raw = statusById.get(id) ?? statusById.get(String(id));
  if (raw && typeof raw === 'object') return raw;
  return { status: raw };
};

export const countBatchEligibility = (selectedIds, statusById) => {
  let digitador = 0;
  let inspetor = 0;
  let antecipada = 0;
  (selectedIds || []).forEach((id) => {
    const meta = getRowMeta(statusById, id);
    if (isDigitadorBatchStatus(meta.status)) digitador += 1;
    if (isInspetorBatchStatus(meta.status)) inspetor += 1;
    if (isAntecipadaBatchEligible(meta)) antecipada += 1;
  });
  return {
    total: (selectedIds || []).length,
    digitador,
    inspetor,
    antecipada,
  };
};

export const isAntecipadaBatchEligible = (meta = {}) =>
  isInspectionFieldStatus(meta.status)
  && isTipoServicoDigitacaoAntecipada(meta.tipoServicoId)
  && !meta.digitacaoAntecipada;

const isEligibleForKind = (meta, kind) => {
  if (kind === 'inspetor') return isInspetorBatchStatus(meta.status);
  if (kind === 'antecipada') return isAntecipadaBatchEligible(meta);
  return isDigitadorBatchStatus(meta.status);
};

export const partitionBatchSelection = (selectedIds, statusById, kind) => {
  const eligibleIds = [];
  (selectedIds || []).forEach((id) => {
    const meta = getRowMeta(statusById, id);
    if (isEligibleForKind(meta, kind)) eligibleIds.push(id);
  });
  return {
    eligibleIds,
    ignoredCount: (selectedIds || []).length - eligibleIds.length,
  };
};

const formatAllowedStatusList = (kind) => {
  const statuses = kind === 'inspetor'
    ? INSPETOR_BATCH_STATUSES
    : DIGITADOR_BATCH_STATUSES;
  return statuses
    .map((status) => Status.STATUS_OPTIONS_DESCRIPTION[status] || status)
    .join(', ');
};

export const getBatchValidationMessage = (partition, kind) => {
  if (!partition?.ignoredCount) return null;
  if (kind === 'antecipada') {
    return 'Alguma NSIR selecionada não pode receber digitação antecipada. É preciso estar em inspeção de campo, ser uso mútuo ou MT/BT e ainda sem o overlay.';
  }
  return `Alguma NSIR selecionada não possui os status permitidos, que são ${formatAllowedStatusList(kind)}.`;
};

const resolveDigitadorId = (meta = {}) => {
  if (meta.digitadorId) return meta.digitadorId;
  if (meta.digitador && typeof meta.digitador === 'object') return meta.digitador.id || null;
  return null;
};

const resolveNsirLabel = (meta = {}, fallbackId) => {
  const codigo = typeof meta.codigo === 'string' ? meta.codigo.trim() : '';
  if (codigo) return codigo;
  const equipamento = typeof meta.equipamento === 'string' ? meta.equipamento.trim() : '';
  if (equipamento) return equipamento;
  if (fallbackId) return String(fallbackId).slice(0, 8);
  return '—';
};

export const getAntecipadaDigitadorSummary = (eligibleIds, statusById) => {
  const withLabels = [];
  const withoutLabels = [];
  (eligibleIds || []).forEach((id) => {
    const meta = getRowMeta(statusById, id);
    const label = resolveNsirLabel(meta, id);
    if (resolveDigitadorId(meta)) {
      withLabels.push(label);
    } else {
      withoutLabels.push(label);
    }
  });
  return {
    withCount: withLabels.length,
    withoutCount: withoutLabels.length,
    withLabels,
    withoutLabels,
  };
};

export const buildAntecipadaLotes = ({
  eligibleIds,
  statusById,
  trocarTodos,
  digitadorId,
}) => {
  const ids = eligibleIds || [];
  if (ids.length === 0) return [];
  if (trocarTodos) {
    if (!digitadorId) return [];
    return [{ nsirIds: ids, digitadorId, liberar: true }];
  }
  const byDigitador = new Map();
  const withoutIds = [];
  ids.forEach((id) => {
    const meta = getRowMeta(statusById, id);
    const currentId = resolveDigitadorId(meta);
    if (currentId) {
      const group = byDigitador.get(currentId) || [];
      group.push(id);
      byDigitador.set(currentId, group);
    } else {
      withoutIds.push(id);
    }
  });
  const lotes = [];
  byDigitador.forEach((nsirIds, currentDigitadorId) => {
    lotes.push({ nsirIds, digitadorId: currentDigitadorId, liberar: true });
  });
  if (withoutIds.length > 0) {
    if (!digitadorId) return lotes;
    lotes.push({ nsirIds: withoutIds, digitadorId, liberar: true });
  }
  return lotes;
};

